/**
 * Rendu du monde : lit l'état de la simulation et ses événements, interpole entre deux ticks.
 * Ne modifie jamais la simulation.
 */
import {
  Color3,
  Color4,
  GlowLayer,
  Mesh,
  PBRMaterial,
  MeshBuilder,
  Quaternion,
  Scene,
  StandardMaterial,
  TrailMesh,
  TransformNode,
  Vector3,
  type AbstractEngine,
  type InstancedMesh,
} from '@babylonjs/core';
import type { GameData, ShipProfile } from '../data';
import { Quat, Vec3 } from '../sim/core/math';
import type { Pickup, Projectile, Ship } from '../sim/entities';
import type { SimEvent } from '../sim/events';
import type { Simulation } from '../sim/simulation';
import type { Quality, Settings } from '../ui/settings';
import { CameraRig } from './camera';
import { DockView } from './dockView';
import { FortressView } from './fortressView';
import { JumpSequence } from './jumpFx';
import { stationArm } from '../sim/world/station';
import { loadSurfaceDetail } from './surfaceDetail';
import { Environment, SceneLights } from './environment';
import { ModelLibrary, type ModelKey } from './models';
import { RenderFx } from './postfx';
import { Vfx, placeBeam } from './vfx';

const v = (a: readonly number[]) => new Vector3(a[0], a[1], a[2]);
type RGB = [number, number, number];

const ROLE_COLOR: Record<string, RGB> = {
  interceptor: [1, 0.18, 0.1],
  marksman: [1, 0.72, 0.15],
  heavy: [1, 0.5, 0.05],
  drone: [1, 0.5, 0.08],
  turret: [1, 0.2, 0.06],
  convoy: [0.3, 1, 0.6],
  boss: [0.9, 0.12, 1],
  player: [0, 0.85, 1],
};

interface ShipView {
  /** Pose de la simulation (interpolée). */
  root: TransformNode;
  /** Inclinaison purement visuelle en virage et glissement, sous la pose. */
  bank: TransformNode;
  trails: Mesh[];
  trailLocal: Vector3[];
  roll: number;
  tilt: number;
  /** Tourelle de forteresse : socle fixe sur son emplacement, tête et socle détaillés ou distants. */
  turret?: TurretParts;
}

interface TurretParts {
  mount: TransformNode;
  near: TransformNode[];
  far: TransformNode[];
  isFar: boolean;
}

/** Au-delà de cette distance (m), une tourelle bascule sur sa version distante (≈ 5 000 triangles au lieu de 22 000). */
const TURRET_FAR_DISTANCE = 150;

interface ProjectileView {
  node: TransformNode;
  trail: TrailMesh | null;
  kind: Projectile['kind'];
}

interface Snapshot {
  position: Vec3;
  rotation: Quat | null;
}

export class WorldRenderer {
  readonly scene: Scene;
  readonly rig: CameraRig;
  readonly models: ModelLibrary;
  readonly vfx: Vfx;
  environment!: Environment;
  readonly lights: SceneLights;
  /** Chaîne de rendu HDR, ombres, occlusion, soleil (palier A). */
  readonly fx: RenderFx;
  dockViews: DockView[] = [];
  /** Forteresses par zone (phase D). */
  readonly fortressViews = new Map<string, FortressView>();
  zone = '';
  /** Séquence de saut entre zones (porteur, chasseur). */
  readonly jump: JumpSequence;
  private glow: GlowLayer;
  private sim: Simulation | null = null;
  private readonly ships = new Map<number, ShipView>();
  private readonly projectiles = new Map<number, ProjectileView>();
  private readonly pickups = new Map<number, TransformNode>();
  /** Socles des tourelles détruites, laissés en place jusqu'au changement de partie. */
  private readonly wrecks: TransformNode[] = [];
  private readonly previous = new Map<number, Snapshot>();
  private readonly templates = new Map<string, Mesh>();
  private trailMaterials = new Map<string, StandardMaterial>();
  private settings: Settings;
  private time = 0;
  /** Positions interpolées de l'image courante (pour le HUD et les effets qui suivent un vaisseau). */
  readonly renderPositions = new Map<number, Vector3>();

  constructor(engine: AbstractEngine, private data: GameData, settings: Settings) {
    this.settings = settings;
    this.scene = new Scene(engine);
    this.scene.useRightHandedSystem = true;
    this.scene.clearColor = new Color4(0.005, 0.008, 0.02, 1);
    this.scene.skipPointerMovePicking = true;
    this.scene.autoClear = true;
    this.rig = new CameraRig(this.scene, data.camera);
    this.scene.activeCamera = this.rig.camera;
    // Lumières avant les modèles et les effets : voir SceneLights.
    this.lights = new SceneLights(this.scene, data.art);
    this.models = new ModelLibrary(this.scene);
    this.vfx = new Vfx(this.scene);
    this.jump = new JumpSequence(this.scene, this.vfx);
    // Texture du halo au rapport de l'écran (une taille fixe carrée étirait les halos en largeur).
    this.glow = new GlowLayer('glow', this.scene, { mainTextureRatio: 0.5, blurKernelSize: 48 });
    this.glow.intensity = 0.9;
    this.fx = new RenderFx(this.scene, [this.rig.camera, this.jump.camera], this.lights.key, data.art);
    // Seuls les matériaux d'effets (Standard, émissifs) alimentent le halo ; les coques PBR (teinte de rôle,
    // textures émissives du boss) restent nettes.
    this.glow.customEmissiveColorSelector = (mesh, _subMesh, material, result) => {
      if (material.name.startsWith('planet') || material.name === 'atmosphere-plasma' || material.name === 'sun' || mesh.name === 'stars') {
        // Étoiles (points) : leur halo, étalé par la texture carrée du halo, en fait des taches floues.
        // Planète, atmosphère, plasma de rentrée : le bord lumineux (Fresnel) ne doit pas faire briller toute la sphère.
        result.set(0, 0, 0, 0);
      } else if (material instanceof StandardMaterial) {
        const c = material.emissiveColor;
        result.set(c.r, c.g, c.b, material.alpha * mesh.visibility);
      } else if (material instanceof PBRMaterial && material.name.endsWith('glow')) {
        // Matériaux lumineux nommés « …glow » dans les modèles (porteur) : participent au halo.
        const c = material.emissiveColor;
        result.set(c.r, c.g, c.b, 1);
      } else {
        result.set(0, 0, 0, 0);
      }
    };
    this.applySettings(settings);
  }

  async load(onProgress?: (done: number, total: number) => void): Promise<string[]> {
    const keys: ModelKey[] = ['taka', 'aegis', 'hydra', 'interceptor', 'marksman', 'heavy', 'drone', 'boss', 'turret', 'vigie', 'stationBay', 'pickup', 'carrier'];
    const missing = await this.models.preload(keys, onProgress);
    // Autant de variantes que d'enveloppes extraites (pas de requêtes 404 pour des fichiers absents).
    const rockCount = await this.models.preloadRocks(this.data.rocks.variants.length || 8);
    this.applyLiveries();
    for (let i = 0; i < rockCount; i++) this.models.rockMaterial(`rock_${i}`, this.data.art.rockAlbedo);
    const detail = loadSurfaceDetail(this.scene);
    this.environment = new Environment(this.scene, this.data, this.models, this.data.rocks.variants.length, this.data.art, this.data.camera.speedStreaks, this.lights, detail);
    this.dockViews = [
      new DockView(this.scene, 'station', this.data.station, this.data.art, this.models, detail, stationArm(this.data)),
      new DockView(this.scene, 'carrier', this.data.carrier, this.data.art, this.models, detail),
    ];
    for (const [id, zone] of Object.entries(this.data.zones.zones)) {
      if (zone.fortress) this.fortressViews.set(id, new FortressView(this.scene, id, zone.fortress, this.data.art, detail));
    }
    this.buildTemplates();
    this.setZone(this.data.zones.home);
    return missing;
  }

  /** Décor, docks et forteresse de la zone (saut du porteur). */
  setZone(id: string): void {
    const zone = this.data.zones.zones[id];
    if (!zone) return;
    this.zone = id;
    this.environment.setZone(id);
    this.fx.setZone(zone);
    for (const view of this.dockViews) view.root.setEnabled(zone.docks.includes(view.id));
    for (const [zoneId, view] of this.fortressViews) view.setEnabled(zoneId === id);
  }

  /** Livrées de faction (art.json) : joueur clair et cyan, ennemis par rôle. */
  applyLiveries(): void {
    const l = this.data.art.liveries;
    const style = this.data.art.liveryStyle;
    const env = this.data.art.shipEnvironmentIntensity;
    for (const key of ['taka', 'aegis', 'hydra'] as const) this.models.livery(key, l.player, style, env);
    for (const key of ['interceptor', 'marksman', 'heavy', 'drone', 'boss'] as const) this.models.livery(key, l[key], style, env);
  }

  applySettings(settings: Settings): void {
    this.settings = settings;
    this.rig.reducedMotion = settings.reducedCameraMotion;
    this.vfx.reducedFlashes = settings.reducedFlashes;
    const q: Record<Quality, { scaling: number; glow: boolean; particles: number }> = {
      low: { scaling: 1.6, glow: false, particles: 0.4 },
      medium: { scaling: 1.25, glow: true, particles: 0.7 },
      high: { scaling: 1, glow: true, particles: 1 },
      epic: { scaling: 1 / Math.min(window.devicePixelRatio || 1, 2), glow: true, particles: 1.3 },
    };
    const preset = q[settings.quality];
    this.scene.getEngine().setHardwareScalingLevel(preset.scaling);
    this.glow.isEnabled = preset.glow;
    this.glow.intensity = settings.reducedFlashes ? 0.45 : 0.9;
    this.fx.applyQuality(settings.quality, settings.reducedFlashes);
    this.vfx.particleScale = preset.particles;
  }

  setData(data: GameData): void {
    this.data = data;
    this.rig.setData(data.camera);
    this.applyLiveries();
  }

  private buildTemplates(): void {
    const s = this.scene;
    const make = (key: string, mesh: Mesh, color: RGB) => {
      mesh.material = this.vfx.material(color);
      mesh.isPickable = false;
      mesh.setEnabled(false);
      this.templates.set(key, mesh);
    };
    const bolt = (key: string, length: number, width: number, color: RGB) => {
      const m = MeshBuilder.CreateCapsule(`tpl-${key}`, { height: length, radius: width / 2, tessellation: 8, subdivisions: 1 }, s);
      m.rotation.x = Math.PI / 2;
      m.bakeCurrentTransformIntoVertices();
      make(key, m, color);
    };
    bolt('bolt-interceptor', 4.5, 0.55, ROLE_COLOR.interceptor!);
    bolt('bolt-heavy', 4.2, 0.75, [1, 0.06, 0.03]);
    bolt('railgun', 26, 0.45, [1, 0.92, 0.62]);
    bolt('volley', 5, 1, [1, 0.3, 0.02]);
    bolt('special', 7, 1.6, [0.75, 0.05, 1]);
    bolt('bossBolt', 5, 0.9, [0.85, 0.05, 1]);
    const missile = MeshBuilder.CreateCylinder('tpl-missile', { height: 1.6, diameterTop: 0, diameterBottom: 0.45, tessellation: 8 }, s);
    missile.rotation.x = -Math.PI / 2;
    missile.bakeCurrentTransformIntoVertices();
    make('missile', missile, [1, 0.55, 0.2]);
    const swarm = MeshBuilder.CreateCylinder('tpl-swarm', { height: 1.3, diameterTop: 0, diameterBottom: 0.4, tessellation: 6 }, s);
    swarm.rotation.x = -Math.PI / 2;
    swarm.bakeCurrentTransformIntoVertices();
    make('swarm', swarm, [1, 0.4, 0.08]);
  }

  /** Démarre l'affichage d'une nouvelle session. */
  attach(sim: Simulation): void {
    this.detach();
    this.sim = sim;
    this.setZone(sim.zone);
    this.rig.reset();
    this.previous.clear();
  }

  /** Changement d'appareil à quai : la vue du joueur sera recréée avec le nouveau modèle. */
  refreshPlayer(): void {
    const id = this.sim?.player.id;
    const view = id !== undefined ? this.ships.get(id) : undefined;
    if (!view || id === undefined) return;
    this.disposeShipView(view);
    this.ships.delete(id);
  }

  /** Supprime la vue d'un vaisseau ; le socle d'une tourelle peut rester en place (épave). */
  private disposeShipView(view: ShipView, keepWreck = false): void {
    if (view.turret) {
      if (keepWreck) {
        for (const n of view.turret.far) n.setEnabled(false);
        for (const n of view.turret.near) n.setEnabled(n.parent === view.turret.mount);
        this.wrecks.push(view.turret.mount);
      } else {
        view.turret.mount.dispose(false, false);
      }
    }
    view.root.dispose(false, false);
  }

  detach(): void {
    for (const view of this.ships.values()) this.disposeShipView(view);
    for (const wreck of this.wrecks) wreck.dispose(false, false);
    this.wrecks.length = 0;
    for (const view of this.projectiles.values()) this.disposeProjectile(view);
    for (const node of this.pickups.values()) node.dispose();
    this.ships.clear();
    this.projectiles.clear();
    this.pickups.clear();
    this.renderPositions.clear();
    this.vfx.clear();
    this.sim = null;
  }

  carrierView(): DockView {
    return this.dockViews.find((v) => v.id === 'carrier')!;
  }

  /** Nœud de la vue du joueur, créée au besoin (séquence de saut du chasseur). */
  playerRoot(): TransformNode | null {
    const sim = this.sim;
    if (!sim) return null;
    if (!this.ships.has(sim.player.id)) this.update(1, 0);
    return this.ships.get(sim.player.id)?.root ?? null;
  }

  /** Masque les vaisseaux (le joueur à quai pendant le saut du porteur). */
  hideShips(): void {
    for (const view of this.ships.values()) {
      view.root.setEnabled(false);
      view.turret?.mount.setEnabled(false);
    }
  }

  /** Image pendant une séquence de saut : simulation suspendue, caméra de la séquence. */
  updateJump(dt: number): void {
    this.time += dt;
    this.jump.update(dt);
    this.scene.activeCamera = this.jump.camera;
    const cam = this.jump.camera.position;
    this.environment.update(dt, cam, cam);
    for (const view of this.dockViews) if (view.root.isEnabled()) view.update(dt, null);
    this.fortressViews.get(this.zone)?.update(dt);
    this.vfx.update(dt, (id) => this.renderPositions.get(id) ?? null);
  }

  /** À appeler avant chaque tick : mémorise l'état précédent pour l'interpolation. */
  capturePrevious(): void {
    const sim = this.sim;
    if (!sim) return;
    for (const ship of sim.state.ships) this.previous.set(ship.id, { position: ship.position.clone(), rotation: ship.rotation.clone() });
    for (const p of sim.state.projectiles) this.previous.set(p.id, { position: p.position.clone(), rotation: null });
    for (const p of sim.state.pickups) this.previous.set(p.id, { position: p.position.clone(), rotation: null });
  }

  private interpolated(id: number, position: Vec3, rotation: Quat | null, alpha: number): { p: Vector3; q: Quaternion | null } {
    const prev = this.previous.get(id);
    const p = prev ? prev.position.lerp(position, alpha) : position;
    let q: Quat | null = rotation;
    if (rotation && prev?.rotation) q = prev.rotation.slerp(rotation, alpha);
    return { p: new Vector3(p.x, p.y, p.z), q: q ? new Quaternion(q.x, q.y, q.z, q.w) : null };
  }

  handleEvents(events: SimEvent[]): void {
    const vfx = this.vfx;
    for (const e of events) {
      switch (e.type) {
        case 'laserFired':
          for (const muzzle of e.muzzles) {
            vfx.beam(v(muzzle), v(e.end), e.color, e.width, e.duration);
            vfx.flash(v(muzzle), e.color, e.width * 4, 0.07);
          }
          break;
        case 'impact': {
          const color: RGB = e.surface === 'rock' ? [1, 0.6, 0.3]
            : e.surface === 'player' ? [1, 0.25, 0.1]
            : e.surface === 'heavy' ? ROLE_COLOR.heavy!
            : e.surface === 'boss' ? ROLE_COLOR.boss! : [0.3, 0.9, 1];
          const big = e.heavy ? 1.8 : 1;
          const at = v(e.position).add(v(e.normal).scale(0.3));
          vfx.flash(at, e.heavy ? [1, 0.45, 0.08] : color, 1.2 * big, 0.18);
          // Étincelles projetées le long de la surface touchée.
          vfx.sparks(at, e.surface === 'rock' ? [1, 0.65, 0.3] : [1, 0.75, 0.4], e.heavy ? 22 : 10, 28 * big, 0.35, v(e.normal));
          break;
        }
        case 'explosion': {
          // Explosions plus discrètes près de la caméra : elles ne doivent pas masquer le combat.
          const near = Vector3.Distance(v(e.position), this.rig.camera.position);
          const k = this.data.art.explosionScale * (e.weapon === 'detonation' ? 0.6 : 1) * Math.min(1, 0.35 + near / 60);
          const swarm = e.weapon === 'swarm';
          vfx.flash(v(e.position), e.color, e.radius * k, e.duration, 1.3);
          vfx.flash(v(e.position), [1, 0.9, 0.6], e.radius * 0.4 * k, e.duration * 0.45, 1.1);
          if (!swarm) vfx.ring(v(e.position), e.color, e.radius * 1.4 * k, 0.45);
          vfx.burst(v(e.position), e.color, swarm ? 18 : 40, e.radius * 6 * k, 2.4 * k, 0.7);
          vfx.sparks(v(e.position), [1, 0.7, 0.35], swarm ? 8 : 24, e.radius * 9 * k, 0.6);
          if (!swarm) vfx.smoke(v(e.position), 8, e.radius * 1.2 * k, 2.4);
          vfx.light(v(e.position), e.color, swarm ? 1.5 : 3, e.radius * 9 * k, e.duration);
          break;
        }
        case 'convoy':
          // Arrivée du convoi : il entame sa descente (éclair de saut).
          if (e.phase === 'arrived') {
            vfx.flash(v(e.position), [0.45, 0.72, 1], 60, 0.8, 1.6);
            vfx.light(v(e.position), [0.45, 0.72, 1], 4, 400, 0.8);
          }
          break;
        case 'enemyWithdrew':
          // Fin de mission : les ennemis restants se replient en saut.
          vfx.flash(v(e.position), [0.7, 0.45, 1], 9, 0.5, 2.2);
          vfx.light(v(e.position), [0.7, 0.45, 1], 2, 120, 0.5);
          break;
        case 'shipDestroyed': {
          const scale = (e.kind === 'boss' || e.kind === 'convoy' ? 3 : e.kind === 'heavy' || e.kind === 'turret' ? 1.5 : e.kind === 'drone' ? 0.7 : 1) * this.data.art.explosionScale;
          const color: RGB = e.kind === 'boss' ? [1, 0.3, 1] : e.kind === 'player' ? [0.4, 0.9, 1] : [1, 0.45, 0.1];
          const at = v(e.position);
          vfx.flash(at, color, e.radius * 2.6 * scale, 0.8, 1.6);
          vfx.flash(at, [1, 0.95, 0.75], e.radius * 1.2 * scale, 0.35, 1.2);
          vfx.ring(at, color, e.radius * 3.5 * scale, 0.6);
          vfx.burst(at, color, 70 * scale, 60 * scale, 3.5 * scale, 1.1);
          vfx.sparks(at, [1, 0.75, 0.4], 50 * scale, 70 * scale, 1.1);
          vfx.smoke(at, 14, e.radius * 2.2 * scale, 3.5);
          vfx.light(at, color, 5 * scale, 180 * scale, 0.9);
          // Gros vaisseaux : explosions secondaires en chaîne sur la carcasse.
          const chain = e.kind === 'boss' || e.kind === 'convoy' ? 6 : e.kind === 'heavy' || e.kind === 'turret' ? 3 : 0;
          for (let i = 0; i < chain; i++) {
            const offset = new Vector3(Math.random() - 0.5, Math.random() - 0.5, Math.random() - 0.5).scale(e.radius * 3 * scale);
            vfx.later(0.12 + i * 0.16 + Math.random() * 0.1, () => {
              const p = at.add(offset);
              vfx.flash(p, [1, 0.55, 0.15], e.radius * 1.1 * scale, 0.45, 1.5);
              vfx.sparks(p, [1, 0.7, 0.3], 16, 40 * scale, 0.7);
              vfx.light(p, [1, 0.5, 0.15], 2.5 * scale, 90 * scale, 0.4);
            });
          }
          const view = this.ships.get(e.shipId);
          if (view && e.kind !== 'player') {
            this.disposeShipView(view, true);
            this.ships.delete(e.shipId);
          } else if (view) {
            view.root.setEnabled(false);
          }
          break;
        }
        case 'damage':
          if (e.targetKind !== 'player' && e.applied > 0) {
            const color = ROLE_COLOR[e.targetKind] ?? [1, 1, 1];
            vfx.flash(v(e.position), [color[0] * 0.5 + 0.5, color[1] * 0.5 + 0.5, color[2] * 0.5 + 0.5], 1.6, 0.16);
          } else if (e.targetKind === 'player' && e.absorbed > 0) {
            // Impact encaissé par le bouclier : éclat bleu au point touché.
            vfx.flash(v(e.position), [0.35, 0.7, 1], 2.4, 0.2, 1.3);
          }
          break;
        case 'telegraph':
          for (const lane of e.lanes) {
            const origin = v(lane.origin), end = v(lane.end);
            const mid = origin.add(end).scale(0.5);
            vfx.beam(origin, end, e.color, e.kind === 'railgun' ? 0.12 : 0.22, e.duration, {
              pulse: true,
              follow: e.follow ? { shipId: e.shipId, anchor: v(e.anchor), base: mid } : null,
            });
          }
          if (e.kind === 'railgun') vfx.flash(v(e.lanes[0]!.origin), e.color, 1.4, e.duration, 1.1);
          break;
        case 'enemyFired': {
          const origin = v(e.origin), dir = v(e.direction);
          if (e.kind === 'railgun') {
            vfx.beam(origin, origin.add(dir.scale(22)), e.color, 0.36, 0.16);
            vfx.beam(origin, origin.add(dir.scale(25)), [1, 0.92, 0.62], 0.1, 0.2);
            vfx.flash(origin, [1, 0.92, 0.62], 2.2, 0.18);
          } else {
            vfx.beam(origin, origin.add(dir.scale(3.2)), e.color, e.source === 'boss' ? 0.26 : 0.16, 0.08);
          }
          break;
        }
        case 'pickupCollected': {
          const node = this.pickups.get(e.pickupId);
          if (node) vfx.flash(node.position.clone(), [1, 0.6, 0.15], 2.5, 0.3);
          break;
        }
        case 'bossPhase': {
          const boss = this.sim?.mission.bossId;
          const pos = boss !== null && boss !== undefined ? this.renderPositions.get(boss) : null;
          const color: RGB = e.phase === 'opening' ? [1, 0.08, 0.01] : e.phase === 'assault' ? [1, 0.32, 0.01] : [0.65, 0.02, 1];
          if (pos) {
            vfx.flash(pos.clone(), color, 18, 0.5, 1.4);
            vfx.light(pos.clone(), color, 4, 200, 0.6);
          }
          break;
        }
        default:
          break;
      }
    }
  }

  /** Synchronise les vues avec l'état courant ; `alpha` interpole entre le tick précédent et le courant. */
  update(alpha: number, frameDt: number): void {
    const sim = this.sim;
    this.time += frameDt;
    if (!sim) return;
    const state = sim.state;
    this.renderPositions.clear();

    const seenShips = new Set<number>();
    for (const ship of state.ships) {
      seenShips.add(ship.id);
      let view = this.ships.get(ship.id);
      if (!view) {
        if (!ship.alive) continue;
        view = this.createShipView(ship);
        this.ships.set(ship.id, view);
      }
      if (ship.kind === 'player') view.root.setEnabled(ship.alive);
      const { p, q } = this.interpolated(ship.id, ship.position, ship.rotation, alpha);
      view.root.position.copyFrom(p);
      view.root.rotationQuaternion!.copyFrom(q!);
      this.renderPositions.set(ship.id, p);
      if (ship.kind !== 'turret' && ship.kind !== 'convoy') this.updateBank(ship, view, frameDt);
      if (view.turret) this.setTurretFar(view.turret, Vector3.Distance(this.rig.camera.position, p) > TURRET_FAR_DISTANCE);
      this.updateTrails(ship, view);
    }
    for (const [id, view] of this.ships) {
      if (!seenShips.has(id)) {
        this.disposeShipView(view);
        this.ships.delete(id);
      }
    }

    const seenProjectiles = new Set<number>();
    for (const p of state.projectiles) {
      if (!p.alive) continue;
      seenProjectiles.add(p.id);
      let view = this.projectiles.get(p.id);
      if (!view) {
        view = this.createProjectileView(p);
        this.projectiles.set(p.id, view);
      }
      const { p: pos } = this.interpolated(p.id, p.position, null, alpha);
      view.node.position.copyFrom(pos);
      const dir = p.velocity.normalized(new Vec3(0, 0, -1));
      const q = Quat.lookRotation(dir);
      view.node.rotationQuaternion!.set(q.x, q.y, q.z, q.w);
    }
    for (const [id, view] of this.projectiles) {
      if (!seenProjectiles.has(id)) {
        this.disposeProjectile(view);
        this.projectiles.delete(id);
      }
    }

    const seenPickups = new Set<number>();
    for (const pickup of state.pickups) {
      seenPickups.add(pickup.id);
      let node = this.pickups.get(pickup.id);
      if (!node) {
        node = this.createPickupView(pickup);
        this.pickups.set(pickup.id, node);
      }
      const { p } = this.interpolated(pickup.id, pickup.position, null, alpha);
      node.position.copyFrom(p);
      node.rotation.y += frameDt * 1.6;
      node.rotation.x += frameDt * 0.7;
      // Clignote pendant ses 3 dernières secondes.
      node.setEnabled(pickup.life > 3 || Math.sin(this.time * 18) > -0.2);
    }
    for (const [id, node] of this.pickups) {
      if (!seenPickups.has(id)) {
        node.dispose();
        this.pickups.delete(id);
      }
    }

    const player = state.player;
    const playerPos = this.renderPositions.get(player.id) ?? v(player.position.toArray());
    const prev = this.previous.get(player.id);
    const rotation = prev?.rotation ? prev.rotation.slerp(player.rotation, alpha) : player.rotation;
    this.rig.update(
      { position: new Vec3(playerPos.x, playerPos.y, playerPos.z), rotation, velocity: player.velocity, flight: player.flight, motor: player.motor, angularVelocity: player.angularVelocity },
      frameDt,
    );
    this.environment.update(frameDt, this.rig.camera.position, playerPos, new Vector3(player.velocity.x, player.velocity.y, player.velocity.z));
    const flying = sim.control.mode === 'flight' && player.alive;
    for (const view of this.dockViews) {
      const dock = sim.docks.find((d) => d.id === view.id);
      if (dock) view.update(frameDt, flying ? dock.readiness(player) : null);
    }
    this.fortressViews.get(sim.zone)?.update(frameDt);
    this.vfx.update(frameDt, (id) => this.renderPositions.get(id) ?? null);
  }

  private createShipView(ship: Ship): ShipView {
    const key: ModelKey = ship.kind === 'player' ? this.sim!.state.weapons.shipId : ship.kind;
    const root = new TransformNode(`ship-${ship.id}`, this.scene);
    root.rotationQuaternion = new Quaternion();
    const bank = new TransformNode(`ship-bank-${ship.id}`, this.scene);
    bank.parent = root;
    bank.rotationQuaternion = new Quaternion();
    const model = this.models.instantiate(key, `ship-model-${ship.id}`);
    model.parent = bank;
    if (ship.kind !== 'player' && ship.kind !== 'boss' && ship.kind !== 'convoy') model.scaling.setAll(this.data.enemies.roles[ship.kind].modelScale);
    const turret = ship.kind === 'turret' ? this.mountTurret(ship, model) : undefined;
    const trails: Mesh[] = [];
    const trailLocal: Vector3[] = [];
    const engines: number[][] = ship.kind === 'player'
      ? (this.data.ships.ships[this.sim!.state.weapons.shipId] as ShipProfile).hardpoints.engines
      : ship.kind === 'turret' ? [] : ship.kind === 'convoy' ? [[-6, 0, 60], [6, 0, 60]] : [[0, 0, ship.radius * 1.15]];
    for (const [i, engine] of engines.entries()) {
      const trail = MeshBuilder.CreateCylinder(`trail-${ship.id}-${i}`, { height: 1, diameterTop: 0.15, diameterBottom: 1, tessellation: 10 }, this.scene);
      trail.rotation.x = Math.PI / 2;
      trail.bakeCurrentTransformIntoVertices();
      trail.parent = bank;
      trail.isPickable = false;
      trails.push(trail);
      trailLocal.push(v(engine));
    }
    return { root, bank, trails, trailLocal, roll: 0, tilt: 0, turret };
  }

  /**
   * Tourelle (glb turret_base / turret_head, + _lod1) : la tête, dont l'origine est au centre de la boule, reste sous
   * la pose de la simulation (la visée) ; le socle passe sous un nœud fixe aligné sur la face d'appui. Le modèle
   * est abaissé de la hauteur de la boule pour que le pivot de la tête tombe sur la position du vaisseau.
   */
  private mountTurret(ship: Ship, model: TransformNode): TurretParts | undefined {
    const normal = this.sim?.fortress?.mountNormal(ship.position);
    const nodes = model.getDescendants(false) as TransformNode[];
    const find = (suffix: string) => nodes.find((n) => n.name.endsWith(suffix));
    const head = find('turret_head');
    const base = find('turret_base');
    if (!normal || !head || !base) return undefined;
    const headFar = find('turret_head_lod1');
    const baseFar = find('turret_base_lod1');
    model.computeWorldMatrix(true);
    head.computeWorldMatrix(true);
    model.position.y = -(head.getAbsolutePosition().y - model.getAbsolutePosition().y);
    model.computeWorldMatrix(true);
    for (const n of nodes) n.computeWorldMatrix(true);
    const mount = new TransformNode(`turret-mount-${ship.id}`, this.scene);
    for (const n of [base, baseFar]) n?.setParent(mount);
    mount.position.set(ship.position.x, ship.position.y, ship.position.z);
    mount.rotationQuaternion = Quaternion.Identity();
    Quaternion.FromUnitVectorsToRef(Vector3.Up(), new Vector3(normal.x, normal.y, normal.z), mount.rotationQuaternion);
    const parts: TurretParts = { mount, near: [head, base], far: [headFar, baseFar].filter((n): n is TransformNode => Boolean(n)), isFar: true };
    this.setTurretFar(parts, false);
    return parts;
  }

  private setTurretFar(parts: TurretParts, far: boolean): void {
    if (parts.isFar === far || parts.far.length === 0) return;
    parts.isFar = far;
    for (const n of parts.near) n.setEnabled(!far);
    for (const n of parts.far) n.setEnabled(far);
  }

  /**
   * Inclinaison visuelle : roulis dans le sens du virage et du glissement, nez qui suit le tangage.
   * N'affecte ni la simulation ni les tirs (camera.json → bank).
   */
  private updateBank(ship: Ship, view: ShipView, dt: number): void {
    const b = this.data.camera.bank;
    const w = ship.angularVelocity;
    const lateral = ship.rotation.conjugate().rotate(ship.velocity).x;
    const max = b.maxDeg * (Math.PI / 180);
    const targetRoll = Math.max(-max, Math.min(max, (w.y * b.fromYaw - lateral * b.fromLateral) * (Math.PI / 180)));
    const tiltMax = b.pitchMaxDeg * (Math.PI / 180);
    const targetTilt = Math.max(-tiltMax, Math.min(tiltMax, w.x * b.fromPitch * (Math.PI / 180)));
    const k = Math.min(1, dt * b.response);
    view.roll += (targetRoll - view.roll) * k;
    view.tilt += (targetTilt - view.tilt) * k;
    Quaternion.RotationYawPitchRollToRef(0, view.tilt, view.roll, view.bank.rotationQuaternion!);
  }

  private trailMaterial(color: RGB, alpha: number): StandardMaterial {
    const key = `${color.join(',')}:${alpha.toFixed(2)}`;
    let m = this.trailMaterials.get(key);
    if (!m) {
      m = new StandardMaterial(`trail-${key}`, this.scene);
      m.disableLighting = true;
      m.emissiveColor = new Color3(...color);
      m.alpha = alpha;
      this.trailMaterials.set(key, m);
    }
    return m;
  }

  /** Traînées moteur : cyan en vol, bleu en boost, orange en frein/recul (UpdateEngineTrails). */
  private updateTrails(ship: Ship, view: ShipView): void {
    const f = ship.flight;
    const m = ship.motor;
    const forward = f.signedForwardSpeed;
    let color: RGB;
    let length: number;
    let radius: number;
    let opacity: number;
    if (ship.kind === 'player') {
      const forwardAlpha = Math.min(1, Math.max(0, forward) / Math.max(m.boostMaxSpeed, 1));
      const reverseAlpha = Math.min(1, Math.max(0, -forward) / Math.max(m.reverseMaxSpeed, 1));
      const reverse = f.braking || f.forwardInput < -0.1 || forward < -3;
      const boost = f.boosting || forward > m.cruiseMaxSpeed + 3;
      length = reverse ? 0.5 + 0.32 * reverseAlpha : 0.72 + ((boost ? 5.8 : 3.2) - 0.72) * forwardAlpha;
      radius = reverse ? 0.07 : boost ? 0.115 : 0.052 + 0.038 * forwardAlpha;
      color = reverse ? [1, 0.28, 0.03] : boost ? [0.22, 0.58, 1] : [0, 0.85, 1];
      opacity = (reverse ? 0.48 + 0.34 * reverseAlpha : 0.42 + 0.56 * forwardAlpha) * (this.settings.reducedFlashes ? 0.72 : 1);
      length *= 2.08;
      radius *= 4.167 * 2;
    } else {
      const alpha = Math.min(1, Math.max(0, forward) / Math.max(m.cruiseMaxSpeed, 1));
      color = ROLE_COLOR[ship.kind] ?? [1, 0.3, 0.1];
      const drone = ship.kind === 'drone';
      length = (1 + 5 * alpha) * (ship.radius / 3.5) * (drone ? 3 : 1);
      radius = 0.5 * (ship.radius / 3.5) * (drone ? 1.4 : 1);
      opacity = drone ? 0.6 + 0.35 * alpha : 0.35 + 0.5 * alpha;
    }
    const quantized = Math.round(opacity * 10) / 10;
    for (const [i, trail] of view.trails.entries()) {
      const local = view.trailLocal[i]!;
      trail.position.set(local.x, local.y, local.z + length / 2);
      trail.scaling.set(radius, radius, length);
      trail.material = this.trailMaterial(color, quantized);
    }
  }

  private createProjectileView(p: Projectile): ProjectileView {
    let key: string = p.kind;
    if (p.kind === 'bolt') key = p.source === 'heavy' ? 'bolt-heavy' : 'bolt-interceptor';
    const template = this.templates.get(key) ?? this.templates.get('bolt-interceptor')!;
    const node = new TransformNode(`proj-${p.id}`, this.scene);
    node.rotationQuaternion = new Quaternion();
    node.position.set(p.position.x, p.position.y, p.position.z);
    const instance: InstancedMesh = template.createInstance(`proj-mesh-${p.id}`);
    instance.parent = node;
    instance.isPickable = false;
    let trail: TrailMesh | null = null;
    if (p.kind === 'missile' || p.kind === 'swarm') {
      trail = new TrailMesh(`proj-trail-${p.id}`, node, this.scene, { diameter: p.kind === 'swarm' ? 0.55 : 0.6, length: p.kind === 'swarm' ? 26 : 36, autoStart: true });
      trail.material = this.trailMaterial(p.kind === 'swarm' ? [1, 0.25, 0.02] : [1, 0.5, 0.15], 0.6);
      trail.isPickable = false;
    }
    return { node, trail, kind: p.kind };
  }

  private disposeProjectile(view: ProjectileView): void {
    view.trail?.dispose();
    view.node.dispose(false, false);
  }

  private createPickupView(pickup: Pickup): TransformNode {
    const node = this.models.instantiate('pickup', `pickup-${pickup.id}`);
    const scale = pickup.kind === 'arena' ? 2.2 : 1.2;
    node.scaling.setAll(scale);
    const halo = MeshBuilder.CreateSphere(`pickup-halo-${pickup.id}`, { diameter: 3.2, segments: 10 }, this.scene);
    halo.material = this.vfx.material([1, 0.45, 0.05]);
    halo.visibility = 0.25;
    halo.parent = node;
    halo.isPickable = false;
    return node;
  }

  /** Utilitaire de debug : trace l'intention des IA. */
  debugLine(from: Vector3, to: Vector3, color: RGB): void {
    const mesh = MeshBuilder.CreateCylinder('debug', { height: 1, diameter: 1, tessellation: 4 }, this.scene);
    placeBeam(mesh, from, to, 0.15);
    mesh.material = this.vfx.material(color);
    setTimeout(() => mesh.dispose(), 50);
  }

  render(): void {
    this.scene.render();
  }
}
